// Gravação e playback de ghosts (docs/05).
import * as THREE from 'three';
import {
  GHOST_SAMPLE_DIV, GHOST_FLOATS_PER_SAMPLE, UNIT_TO_METERS, fnv1a,
  GhostData, encodeGhost, decodeGhost, gzipBytes, gunzipBytes, bytesToBase64, base64ToBytes,
} from '@surfrun/shared';
import type { PlayerState } from '../physics/move';

// ============ gravação ============
export class GhostRecorder {
  private buf = new Float32Array(4096 * GHOST_FLOATS_PER_SAMPLE);
  private count = 0;
  private tickInRun = 0;

  reset(): void {
    this.count = 0;
    this.tickInRun = 0;
  }

  /** Chamar todo tick enquanto a run está 'running'. */
  record(p: PlayerState, yaw: number, pitch: number): void {
    if (this.tickInRun % GHOST_SAMPLE_DIV === 0) {
      if ((this.count + 1) * GHOST_FLOATS_PER_SAMPLE > this.buf.length) {
        const bigger = new Float32Array(this.buf.length * 2);
        bigger.set(this.buf);
        this.buf = bigger;
      }
      const o = this.count * GHOST_FLOATS_PER_SAMPLE;
      this.buf[o] = p.pos.x; this.buf[o + 1] = p.pos.y; this.buf[o + 2] = p.pos.z;
      this.buf[o + 3] = yaw; this.buf[o + 4] = pitch;
      this.count++;
    }
    this.tickInRun++;
  }

  /** Fecha a gravação em um ghost binário gzip (base64). */
  async finish(slug: string, timeMs: number, splits: number[]): Promise<string> {
    const raw = encodeGhost({
      timeMs, slugHash: fnv1a(slug), sampleCount: this.count,
      samples: this.buf, splits,
    });
    return bytesToBase64(await gzipBytes(raw));
  }
}

// ============ playback ============
export type GhostSlot = 'pb' | 'rival1' | 'rival2';
const SLOT_COLOR: Record<GhostSlot, number> = { pb: 0x4fc3f7, rival1: 0xffb74d, rival2: 0xb388ff };

function makeNametag(text: string, color: number): THREE.Sprite {
  const cv = document.createElement('canvas');
  cv.width = 256; cv.height = 56;
  const c = cv.getContext('2d')!;
  c.font = '700 30px Inter, sans-serif';
  c.textAlign = 'center';
  c.fillStyle = '#000a';
  c.fillRect(0, 6, 256, 44);
  c.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  c.fillText(text.slice(0, 16), 128, 38);
  const tex = new THREE.CanvasTexture(cv);
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  sp.scale.set(96, 21, 1); // units
  return sp;
}

export class GhostPlayer {
  group = new THREE.Group();
  data: GhostData;
  username: string;
  slot: GhostSlot;
  private body: THREE.Mesh;

  constructor(data: GhostData, username: string, slot: GhostSlot) {
    this.data = data;
    this.username = username;
    this.slot = slot;
    const color = SLOT_COLOR[slot];
    // corpo: cápsula translúcida em escala de units (playermodels reais na F7)
    const geo = new THREE.CapsuleGeometry(14, 44, 4, 12);
    this.body = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      color, transparent: true, opacity: 0.45, roughness: 0.4,
      emissive: color, emissiveIntensity: 0.25, depthWrite: false,
    }));
    this.body.position.y = 36; // centro da cápsula na metade da altura do hull
    const tag = makeNametag(username, color);
    tag.position.y = 96;
    this.group.add(this.body, tag);
    this.setToStart();
  }

  /** Split do ghost ao ENTRAR no stage N (2..): ms, ou null. */
  splitAtStage(stage: number): number | null {
    return this.data.splits[stage - 2] ?? null;
  }

  setToStart(): void { this.applySample(0); }

  private applySample(f: number): void {
    const n = this.data.sampleCount;
    if (n === 0) return;
    const i0 = Math.min(Math.floor(f), n - 1);
    const i1 = Math.min(i0 + 1, n - 1);
    const t = f - i0;
    const s = this.data.samples;
    const a = i0 * GHOST_FLOATS_PER_SAMPLE, b = i1 * GHOST_FLOATS_PER_SAMPLE;
    const x = s[a]! + (s[b]! - s[a]!) * t;
    const y = s[a + 1]! + (s[b + 1]! - s[a + 1]!) * t;
    const z = s[a + 2]! + (s[b + 2]! - s[a + 2]!) * t;
    // yaw por menor arco
    let dy = s[b + 3]! - s[a + 3]!;
    if (dy > 180) dy -= 360; else if (dy < -180) dy += 360;
    const yaw = s[a + 3]! + dy * t;
    this.group.position.set(x, y, z);
    this.body.rotation.y = (yaw * Math.PI) / 180;
  }

  /** Pose no tick da run local (ticks desde a largada, pode ser fracionário). */
  update(runTicks: number): void {
    this.applySample(runTicks / GHOST_SAMPLE_DIV);
  }

  /** Amostra crua p/ câmera de espectador: [x,y,z,yaw,pitch]. */
  sampleAt(f: number, out: number[]): void {
    const n = this.data.sampleCount;
    const i0 = Math.max(0, Math.min(Math.floor(f), n - 1));
    const i1 = Math.min(i0 + 1, n - 1);
    const t = f - i0;
    const s = this.data.samples;
    const a = i0 * GHOST_FLOATS_PER_SAMPLE, b = i1 * GHOST_FLOATS_PER_SAMPLE;
    for (let k = 0; k < 3; k++) out[k] = s[a + k]! + (s[b + k]! - s[a + k]!) * t;
    let dy = s[b + 3]! - s[a + 3]!;
    if (dy > 180) dy -= 360; else if (dy < -180) dy += 360;
    out[3] = s[a + 3]! + dy * t;
    out[4] = s[a + 4]! + (s[b + 4]! - s[a + 4]!) * t;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.body.geometry.dispose();
  }
}

// ============ PB local (localStorage) ============
const GKEY = (slug: string): string => `surfrun.ghost.${slug}`;

export function saveLocalGhost(slug: string, base64gz: string): void {
  try { localStorage.setItem(GKEY(slug), base64gz); } catch { /* cheio */ }
}

export async function loadLocalGhost(slug: string): Promise<GhostData | null> {
  try {
    const b64 = localStorage.getItem(GKEY(slug));
    if (!b64) return null;
    const raw = await gunzipBytes(base64ToBytes(b64));
    const g = decodeGhost(raw);
    return g && g.slugHash === fnv1a(slug) ? g : null;
  } catch {
    return null;
  }
}

/** Ghost do servidor (resposta já vem descomprimida pelo browser). */
export async function fetchGhost(runId: number): Promise<GhostData | null> {
  try {
    const res = await fetch(`/api/ghosts/${runId}`);
    if (!res.ok) return null;
    return decodeGhost(new Uint8Array(await res.arrayBuffer()));
  } catch {
    return null;
  }
}

export { UNIT_TO_METERS };
